import { Component, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SitesService } from 'src/app/sites/sites.service';
import { SideMenuService } from 'src/app/shared/side-menu/side-menu.service';
import { NotificationMessageService } from 'src/app/shared/notification-message/notification-message.service';
import { Subscription } from 'rxjs';
import moment from 'moment';
import { DeviceFactory } from '../../device-factory/device-factory';
import { AngularCsv } from 'angular-csv-ext/dist/Angular-csv';
import { CommonService } from 'src/app/shared/services/common.service';

@Component({
	selector: 'app-power-usage-charts',
	templateUrl: './power-usage-charts.component.html',
	styleUrls: ['./power-usage-charts.component.css']
})
export class PowerUsageChartsComponent {
	@Input() hasTruckTypesFilter: boolean = false;
	@Input() deviceTypes: any[] = [ DeviceFactory.types.iotah ];
	@Input() deviceInfo:any = null;
	@Input() studyInfo: any = {};
	@Input() config: {
		title: string,
		ampChartTitle: string,
		kwChartTitle: string,
	} = {
		title: this.translateService.instant('perf_analytic.truck_power_usage_charts'),
		ampChartTitle: this.translateService.instant('fleet_reports.truck_amp_draw'),
		kwChartTitle: this.translateService.instant('fleet_reports.truck_kw_draw'),
	};
	@Input() tooltipText = "";
	@Input() useParentFilters: boolean = false;
	@Input() workingDays: string = null;
	@Input() siteUtilizationSettings: any = null;
	@Input() parentFromDate: Date = null;
	@Input() parentToDate: Date = null;

	deviceTypesTooltips: any = {
		[DeviceFactory.types.iotah]: 'power_usage_chart',
		[DeviceFactory.types.charglink]: 'chargLinkDevice.power_usage_chart',
		[DeviceFactory.types.brm]: 'chargLinkDevice.power_usage_chart',
	};

	subSite: Subscription = new Subscription();
	invalidTruckUsageDate: boolean = false;
	siteInfo: any = {};

	truckTypeList: any[] = [{value: 'all', label: this.translateService.instant('site_performance.all_truck_types')}];
	selectedDailyUsageTruckTypeFilter = this.truckTypeList[0].value;

	powerUsageToDate = new Date((new Date()).getTime() - 24 * 60 * 60 * 1000); // yesterday
	yesterday =  new Date((new Date()).getTime() - 24 * 60 * 60 * 1000);
	powerUsageFromDate = new Date((new Date()).getTime() - (30 * 24 * 60 * 60 * 1000)); // 30 days ago
	powerUsageMinDate = moment(0).toDate();

	private readonly quarterSeconds = 15 * 60;
	private readonly minEmptySlotsForOff = 2; // 2 empty 15-minute slots = 30 minutes with no RT records

	powerUsage: any = {
		amp: [],
		kw: [],
		ampColumns: [this.translateService.instant('g.date'), this.translateService.instant('fleet_reports.truck_amp')],
		kwColumns: [this.translateService.instant('g.date'), this.translateService.instant('fleet_reports.truck_kwh')],
		config: {
			hAxis: {
				title: this.translateService.instant('g.date'),
				format: 'yyyy-MM-dd',
				slantedText: false,
			},
			colors: ['#327339'],
			legend: {
				position: 'top',
				alignment: 'center',
				textStyle: { fontSize: 12 }
			},
			titleTextStyle: {
				fontSize: 16,
				bold: true,
				color: '#333',
				paddingBottom: 20,
				paddingTop: 20
			},
		},
		ampConfig: {},
		kwConfig: {},
	};

	get printFilters(): { label: string, value: string }[] {
		const filters: { label: string, value: string }[] = [
			{ label: 'perf_analytic.period', value: `${moment(this.powerUsageFromDate).format('MM/DD/YYYY')} - ${moment(this.powerUsageToDate).format('MM/DD/YYYY')}` }
		];

		if (this.hasTruckTypesFilter)
			filters.push({ label: 'devices.truck_type', value: this.commonService.getOptionLabel(this.truckTypeList, this.selectedDailyUsageTruckTypeFilter) });

		// workingDays silently drops non-working days from the chart data (see getDailyRtData below) - print it so the filtered-out days aren't mistaken for missing data.
		if (this.workingDays)
			filters.push({ label: 'g.working_days', value: this.translateService.instant(this.workingDays === 'workingDays' ? 'g.yes' : 'g.no') });

		return filters;
	}

	constructor(
		private translateService: TranslateService,
		private siteService: SitesService,
		private sideMenuService: SideMenuService,
		private notificationMessageService: NotificationMessageService,
		private commonService: CommonService,
	) {}

	ngOnInit() {
		this.subSite = this.sideMenuService.currentSite.subscribe((site: any) => {
			if(!site['id'])
				return;

			this.siteInfo = site;
			const installation_date = this.deviceInfo?.installation_date || this.siteInfo.installation_date;

			this.powerUsageMinDate = moment.utc(installation_date * 1000).startOf('day').toDate();
			this.getDailyRtData();
		});

		this.updateTitles();
	}

	updateTitles() {
		this.powerUsage.kwColumns = [this.translateService.instant('g.date'), this.config.kwChartTitle || this.translateService.instant('fleet_reports.truck_kwh')];
		this.powerUsage.ampColumns = [this.translateService.instant('g.date'), this.config.ampChartTitle || this.translateService.instant('fleet_reports.truck_amp')];

		this.powerUsage.ampConfig = {
			...this.powerUsage.config,
			vAxis: {
				...this.powerUsage.config.vAxis,
				title: this.config.ampChartTitle || this.translateService.instant('fleet_reports.truck_amp'),
			},
		};

		this.powerUsage.kwConfig = {
			...this.powerUsage.config,
			vAxis: {
				...this.powerUsage.config.vAxis,
				title: this.config.kwChartTitle || this.translateService.instant('fleet_reports.truck_kwh'),
			},
		};
	}

	ngOnChanges(changes) {
		const installation_date = this.deviceInfo?.installation_date || this.siteInfo.installation_date;
		this.powerUsageMinDate = moment.utc(installation_date * 1000).startOf('day').toDate();

		if (this.useParentFilters) {
			if (changes.parentFromDate || changes.parentToDate || changes.workingDays || changes.studyInfo) {
				if (this.parentFromDate)
					this.powerUsageFromDate = this.parentFromDate;

				if (this.parentToDate)
					this.powerUsageToDate = this.parentToDate;

				if (this.powerUsageFromDate && this.powerUsageToDate)
					this.getDailyRtData();
			}
		} else {
			if (changes.deviceInfo?.currentValue?.mac_address)
				this.getDailyRtData();
		}

		if (changes.config?.currentValue)
			this.updateTitles();
	}

	changeDate(type, value) {
		this[type] = value;
		this.getDailyRtData();
	}

	get studyStartedToday(): boolean {
		return !!(this.studyInfo?.id && moment.unix(this.studyInfo.start_study_time).isSame(moment(), 'day'));
	}

	/**
	 * Timestamps of the 15-minute slots to show as Off (0 Amp / 0 kW).
	 * A run of `minEmptySlotsForOff` or more empty slots means no RT records for 30 minutes or more,
	 * which the charts show as zeros instead of drawing a straight line across the gap. Slots before
	 * the first record are skipped - not reporting yet is not the same as being Off.
	 */
	private getOffPeriodSlots(data: Record<string, unknown>, toDate: number): number[] {
		const recordedTimes = Object.keys(data).map(Number).sort((a, b) => a - b);
		if (!recordedTimes.length)
			return [];

		const recorded = new Set(recordedTimes);
		const until = Math.min(toDate, moment().unix());

		const offSlots: number[] = [];
		let emptyRun: number[] = [];

		for (let slot = recordedTimes[0] + this.quarterSeconds; slot <= until; slot += this.quarterSeconds) {
			if (!recorded.has(slot)) {
				emptyRun.push(slot);
				continue;
			}

			if (emptyRun.length >= this.minEmptySlotsForOff)
				offSlots.push(...emptyRun);

			emptyRun = [];
		}

		// a run still open at the end means the devices never resumed - keep them Off
		if (emptyRun.length >= this.minEmptySlotsForOff)
			offSlots.push(...emptyRun);

		return offSlots;
	}

	getDailyRtData() {
		if (this.studyStartedToday) {
			this.powerUsage.amp = [];
			this.powerUsage.kw = [];
			return;
		}

		const zoneDiff	= new Date().getTimezoneOffset() * -1;
		this.invalidTruckUsageDate = false;

		let fromDate = new Date(new Date(this.powerUsageFromDate).setHours(0, 0, 0, 0)).getTime() + (zoneDiff * 60 * 1000);
		fromDate = moment(fromDate).utc().startOf('day').unix();

		let toDate = new Date(new Date(this.powerUsageToDate).setHours(23, 59, 59, 999)).getTime() + (zoneDiff * 60 * 1000)
		toDate = moment(toDate).utc().endOf('day').unix();

		if (fromDate > toDate) {
			this.invalidTruckUsageDate = true;
			return this.notificationMessageService.setMessage('translate|g.invalid_input');;
		}

		const filters = {
			getTruckTypes: this.hasTruckTypesFilter,
			truckType: this.hasTruckTypesFilter ? this.selectedDailyUsageTruckTypeFilter : 'all',
		}

		const deviceTypes = this.deviceTypes || [DeviceFactory.types.iotah];
		let siteId = this.deviceInfo ? 0 : this.siteInfo.id || 0; // ether have device info or site id

		const workingDaysOnly = this.workingDays === 'workingDays';
		const siteWD = this.siteUtilizationSettings?.truck_utilization_working_days || [];

		this.siteService.getDailyRtData(fromDate, toDate, this.deviceInfo?.mac_address || "", deviceTypes, siteId, this.studyInfo.id || 0, filters).subscribe((res: any) => {
			const data = res.data || {};
			this.prepareWidgetsTruckTypes(res);
			this.powerUsage.amp = [];
			this.powerUsage.kw = [];

			const chartTimes = [...Object.keys(data).map(Number), ...this.getOffPeriodSlots(data, toDate)].sort((a, b) => a - b);

			for (const time of chartTimes) {
				if (workingDaysOnly && !this.commonService.isWorkingDay(time, siteWD))
					continue;

				const utcDate = new Date(time * 1000);
				const chartDate = new Date(utcDate.getUTCFullYear(), utcDate.getUTCMonth(), utcDate.getUTCDate(), utcDate.getUTCHours(), utcDate.getUTCMinutes(), utcDate.getUTCSeconds());
				this.powerUsage.amp.push([chartDate, +(data[time]?.amp || 0).toFixed(2)]);
				this.powerUsage.kw.push([chartDate, +(data[time]?.kw || 0).toFixed(2)]);
			}
		});
	}

	prepareWidgetsTruckTypes(response) {
		const deviceTruckTypes = [];
		deviceTruckTypes.push({value: 'all', label: this.translateService.instant('site_performance.all_truck_types')});

		if ('truckTypes' in response) {
			for (const truckType of response.truckTypes) {
				const alreadyExists = deviceTruckTypes.find(item => item.value == truckType);
				if (!alreadyExists)
					deviceTruckTypes.push(truckType);
			}
			delete response.truckTypes;
			this.truckTypeList = deviceTruckTypes;
		}

		return response;
	}

	exportUsage() {
		const headersInfo = this.commonService.getCsvFileHeadersInfo({
			customerName: this.siteInfo.customer_name,
			siteName: this.siteInfo.name,
			deviceSN: this.deviceInfo ? this.deviceInfo.serial_number : null
		});

		const csvData = [...headersInfo];

		csvData.push([this.translateService.instant('g.date'), this.translateService.instant(this.config.ampChartTitle)]);
		for(const data of this.powerUsage.amp) {
			csvData.push([moment(data[0]).format('MM/DD/YYYY hh:mm'), data[1]]);
		}
		csvData.push([]);

		csvData.push([this.translateService.instant('g.date'), this.translateService.instant(this.config.kwChartTitle)]);
		for(const data of this.powerUsage.kw) {
			csvData.push([moment(data[0]).format('MM/DD/YYYY hh:mm'), data[1]]);
		}
		new AngularCsv(csvData, this.translateService.instant(this.config.title));
	}

	ngOnDestroy() {
		this.subSite.unsubscribe();
	}
}
